/**
 * ArtworkDetail.tsx
 * Composant de détail d'une œuvre, entièrement stylisé avec une esthétique africaine artistique
 * pour mettre en valeur les créations d'Ernest Nzofoug.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Calendar, MapPin, Ruler, Layers, 
  ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut, 
  Maximize, Clock, Check, Shapes, Loader2, Palette
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { fetchArtworkBySlug, type ArtWork } from '../../services/portfolioService';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import 'react-medium-image-zoom/dist/styles.css';
import useKeypress from 'react-use-keypress';
import Zoom from 'react-medium-image-zoom';
import { useTenantContext } from '../../context/TenantContext';

/**
 * Animation variants pour Framer Motion
 */
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

const slideIn = {
  hidden: { x: -60, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { duration: 0.8, ease: "easeOut" } }
};

const scaleIn = {
  hidden: { scale: 0.9, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.7, ease: "easeOut" } }
};

export const ArtworkDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { tenant } = useTenantContext();
  
  const [artwork, setArtwork] = useState<ArtWork | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const nextImage = useCallback(() => {
    if (artwork && artwork.gallery_images && artwork.gallery_images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % artwork.gallery_images!.length);
    }
  }, [artwork]);

  const prevImage = useCallback(() => {
    if (artwork && artwork.gallery_images && artwork.gallery_images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + artwork.gallery_images!.length) % artwork.gallery_images!.length);
    }
  }, [artwork]);
  
  const toggleFullscreen = useCallback(() => setIsFullscreen(prev => !prev), []);
  
  useKeypress(['ArrowLeft', 'ArrowRight', 'Escape', 'f'], (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') prevImage();
    else if (event.key === 'ArrowRight') nextImage();
    else if (event.key === 'Escape' && isFullscreen) setIsFullscreen(false);
    else if (event.key === 'f') toggleFullscreen();
  });
  
  // Configuration SEO personnalisée par tenant (doit être avant les returns conditionnels)
  const seoConfig = React.useMemo(() => {
    if (!artwork) return null;
    
    const seo = tenant?.config?.seo || {};
    const currentDomain = window.location.hostname;
    
    return {
      title: `${artwork.title} | ${tenant?.name || 'Portfolio'}`,
      description: artwork.description || `Découvrez "${artwork.title}", une œuvre de ${tenant?.name || 'l\'artiste'}.`,
      image: artwork.featured_image_url,
      url: `https://${currentDomain}/portfolio/${artwork.slug}`
    };
  }, [artwork, tenant]);

  useEffect(() => {
    const loadArtwork = async () => {
      if (!slug) return;
      setLoading(true);
      setError(null);
      try {
        const data = await fetchArtworkBySlug(slug);
        if (!data) throw new Error('Œuvre non trouvée');
        setArtwork(data);
      } catch (err) {
        console.error('Erreur:', err);
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };
    loadArtwork();
  }, [slug]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="bg-[#0C0A08] text-white min-h-screen flex flex-col justify-center items-center">
          <div className="relative">
            <div className="absolute -inset-10 rounded-full opacity-30 bg-gradient-to-r from-amber-500 via-orange-600 to-red-700 blur-xl animate-pulse"></div>
            <Loader2 className="animate-spin h-16 w-16 text-amber-500 relative z-10" />
          </div>
          <span className="mt-8 text-2xl font-serif text-amber-100/90">Chargement de l'œuvre...</span>
        </div>
      </PublicLayout>
    );
  }

  if (error || !artwork) {
    return (
      <PublicLayout>
        <div className="bg-[#0C0A08] text-white min-h-screen flex flex-col justify-center items-center">
          <div className="text-center max-w-md p-8 rounded-lg border border-amber-900/30 bg-gradient-to-b from-black/80 to-amber-950/20">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-amber-900/30 flex items-center justify-center">
              <X className="h-12 w-12 text-amber-500" />
            </div>
            <h2 className="text-3xl font-serif font-bold mb-4 text-amber-100">Œuvre non trouvée</h2>
            <p className="text-amber-200/70 mb-8">L'œuvre que vous recherchez n'existe pas ou a été déplacée.</p>
            <Button asChild variant="default" className="bg-amber-700 hover:bg-amber-600 text-amber-50">
              <Link to="/portfolio">Retour au portfolio</Link>
            </Button>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const galleryImages = artwork.gallery_images && artwork.gallery_images.length > 0 
    ? artwork.gallery_images 
    : [artwork.featured_image_url];

  return (
    <PublicLayout>
      {seoConfig && (
        <SEOMetaTags
          title={seoConfig.title}
          description={seoConfig.description}
          image={seoConfig.image}
          url={seoConfig.url}
        />
      )}

 {/* Fond d'écran avec motifs africains */}
 <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant ? `url(${tenant.config.bg})` : 'url(/default-bg.jpg)',
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-amber-950/95 via-black/90 to-black/95 backdrop-blur-[3px]"></div>
          <div className="absolute inset-0 opacity-20" 
               style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Cpath d='M14 16H9v-2h5V9h2v5h5v2h-5v5h-2v-5zm0 0v5h-2v-5H7v-2h5V9h2v5h5v2h-5zm11 11h-2v-2h2v-2h2v2h2v2h-2v2h-2v-2zm6-6h2v2h-2v2h-2v-2h-2v-2h2v-2h2v2zm0 0v-2h2v2h2v2h-2v2h-2v-2h-2v-2h2zm11 11h2v2h-2v2h-2v-2h-2v-2h2v-2h2v2zm-2-6h-2v-2h2v-2h2v2h2v2h-2v2h-2v-2zm0 0v2h-2v-2h-2v-2h2v-2h2v2h2v2h-2z' fill='%23d97706' fill-opacity='0.2'/%3E%3C/svg%3E")` }}
          ></div>
        </div>
       <div className="bg-transparent text-white pt-20 relative z-10">
        <div className="container mx-auto px-4 py-12">
          <Link to="/portfolio" className="inline-flex items-center text-amber-400/70 hover:text-amber-400 mb-8 transition-colors group">
            <span className="w-8 h-8 mr-3 rounded-full bg-amber-900/30 flex items-center justify-center group-hover:bg-amber-900/50 transition-colors">
              <ArrowLeft className="h-4 w-4" />
            </span>
            Retour aux œuvres
          </Link>

          <motion.article initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.div variants={slideIn} className="relative inline-block mb-10">
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-amber-100 mb-2 relative z-10">
                {artwork.title}
              </h1>
              <div className="absolute -bottom-3 left-0 right-0 h-1 bg-gradient-to-r from-amber-600 to-amber-800"></div>
              <div className="absolute -bottom-3 left-0 w-16 h-1 bg-amber-500 animate-pulse"></div>
            </motion.div>

            {/* Galerie principale avec style africain */}
            <div className="mb-16">
              <AnimatePresence>
                {isFullscreen && (
                  <motion.div 
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
                  >
                    <div className="absolute top-4 right-4 flex gap-2">
                      <Button onClick={toggleFullscreen} variant="ghost" size="icon" className="bg-amber-900/30 hover:bg-amber-900/50 text-amber-100" aria-label="Quitter le mode plein écran">
                        <X size={20} />
                      </Button>
                    </div>
                    
                    <img 
                      src={galleryImages[currentImageIndex]} 
                      alt={`${artwork.title} - Vue ${currentImageIndex + 1}`}
                      className="max-w-[90vw] max-h-[90vh] object-contain"
                    />
                    
                    {galleryImages.length > 1 && (
                      <>
                        <Button onClick={prevImage} variant="ghost" size="icon" className="absolute left-4 top-1/2 -translate-y-1/2 bg-amber-900/30 hover:bg-amber-900/50 text-amber-100" aria-label="Image précédente">
                          <ChevronLeft size={28} />
                        </Button>
                        <Button onClick={nextImage} variant="ghost" size="icon" className="absolute right-4 top-1/2 -translate-y-1/2 bg-amber-900/30 hover:bg-amber-900/50 text-amber-100" aria-label="Image suivante">
                          <ChevronRight size={28} />
                        </Button>
                      </>
                    )}
                    
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-amber-900/50 px-4 py-2 rounded-full text-amber-100 text-sm border border-amber-600/30">
                      {currentImageIndex + 1} / {galleryImages.length}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              <motion.div 
                className="relative group"
                variants={scaleIn}
              >
                <div className="absolute -inset-1 md:-inset-3 bg-black/50 rounded-2xl opacity-80 blur-md group-hover:opacity-100 transition duration-700"></div>
                <div className="relative bg-black rounded-xl overflow-hidden p-1 md:p-2">
                  <Zoom>
                    <img 
                      src={galleryImages[currentImageIndex]} 
                      alt={`${artwork.title} - Vue ${currentImageIndex + 1}`}
                      className="w-full rounded-lg bg-black/20"
                      style={{ objectFit: "contain", aspectRatio: "16/9" }}
                    />
                  </Zoom>
                  
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button onClick={toggleFullscreen} variant="secondary" size="icon" className="bg-amber-900/50 hover:bg-amber-800 text-amber-100" aria-label="Mode plein écran">
                      <Maximize size={18} />
                    </Button>
                  </div>
                  
                  {galleryImages.length > 1 && (
                    <>
                      <Button onClick={prevImage} variant="secondary" size="icon" className="absolute left-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 bg-amber-900/50 hover:bg-amber-800 text-amber-100" aria-label="Image précédente">
                        <ChevronLeft size={24} />
                      </Button>
                      <Button onClick={nextImage} variant="secondary" size="icon" className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 bg-amber-900/50 hover:bg-amber-800 text-amber-100" aria-label="Image suivante">
                        <ChevronRight size={24} />
                      </Button>
                    </>
                  )}
                </div>
              </motion.div>
              
              {galleryImages.length > 1 && (
                <div className="flex gap-3 mt-6 overflow-x-auto pb-3 justify-center">
                  {galleryImages.map((image, index) => (
                    <motion.button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden transition-all duration-300 ${currentImageIndex === index ? 'ring-2 ring-amber-500 scale-110 z-10' : 'opacity-70 hover:opacity-100'}`}
                      whileHover={{ scale: 1.05 }}
                      variants={fadeIn}
                    >
                      <div className={`absolute inset-0 ${currentImageIndex === index ? 'bg-gradient-to-br from-amber-700/30 to-red-900/30' : ''}`}></div>
                      <img 
                        src={image} 
                        alt={`Miniature ${index + 1}`} 
                        className="w-full h-full" 
                        style={{ objectFit: "contain" }}
                        loading="lazy" 
                      />
                    </motion.button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
              <motion.div className="lg:col-span-7 xl:col-span-8" variants={scaleIn}>
                <div className="relative">
                  <div className="absolute -inset-1 bg-black/50 rounded-2xl opacity-80 blur-md"></div>
                  <div className="relative bg-black/40 backdrop-blur-sm rounded-xl border border-amber-900/30 p-8 shadow-lg">
                    <div className="absolute top-0 right-0 w-24 h-24 opacity-10">
                      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="text-amber-500">
                        <path d="M20,20 L80,20 L80,80 L20,80 Z M0,0 L40,0 L40,40 L0,40 Z M60,60 L100,60 L100,100 L60,100 Z" />
                      </svg>
                    </div>
                    <motion.h2 variants={slideIn} className="text-3xl font-serif font-bold text-amber-200 mb-6 flex items-center">
                      <Palette className="mr-3 text-amber-500" />
                      Le Récit de l'Œuvre
                    </motion.h2>
                    {artwork.description && (
                      <motion.blockquote variants={fadeIn} className="text-xl font-medium text-amber-100/90 mb-8 italic border-l-4 border-amber-600 pl-4 py-2 bg-amber-900/10 rounded-r-lg">
                        {artwork.description}
                      </motion.blockquote>
                    )}
                    <motion.div variants={fadeIn}
                      className="prose prose-lg max-w-none prose-headings:text-amber-200 prose-p:text-amber-100/80 prose-strong:text-amber-400 prose-a:text-amber-400 hover:prose-a:underline prose-blockquote:border-amber-600 prose-blockquote:bg-amber-900/10 prose-blockquote:rounded-r-lg"
                      dangerouslySetInnerHTML={{ __html: artwork.content }}
                    />
                  </div>
                </div>
              </motion.div>

              <motion.div className="lg:col-span-5 xl:col-span-4" variants={slideIn}>
                <div className="relative">
                  <div className="absolute -inset-1 bg-black/50 rounded-2xl opacity-80 blur-md"></div>
                  <div className="relative bg-black/40 backdrop-blur-sm rounded-xl border border-amber-900/30 p-8 shadow-lg sticky top-24">
                    <div className="absolute top-0 left-0 w-full h-full overflow-hidden rounded-xl opacity-5 pointer-events-none">
                      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                        <pattern id="africaPattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                          <path d="M0 20 L20 0 L40 20 L20 40 Z" fill="none" stroke="currentColor" strokeWidth="1"/>
                          <circle cx="20" cy="20" r="4" fill="currentColor"/>
                        </pattern>
                        <rect x="0" y="0" width="100%" height="100%" fill="url(#africaPattern)" className="text-amber-500"/>
                      </svg>
                    </div>
                    <h2 className="text-2xl font-serif font-bold text-amber-200 mb-6 flex items-center">
                      <div className="w-10 h-10 rounded-full bg-amber-900/50 flex items-center justify-center mr-3">
                        <Layers className="text-amber-400 h-5 w-5" />
                      </div>
                      Fiche Technique
                    </h2>
                    <ul className="space-y-5">
                      {artwork.categories?.length > 0 && <li><InfoItem icon={Layers} label="Type" data={<div className="flex flex-wrap gap-2">{artwork.categories.map(c => <Badge key={c.id} className="bg-amber-800/80 hover:bg-amber-700 text-amber-100">{c.name}</Badge>)}</div>} /></li>}
                      {artwork.materials?.length > 0 && <li><InfoItem icon={Shapes} label="Matériaux" data={<div className="flex flex-wrap gap-2">{artwork.materials.map(m => <Badge key={m} variant="outline" className="border-amber-700/50 text-amber-200">{m}</Badge>)}</div>} /></li>}
                      {artwork.dimensions && <li><InfoItem icon={Ruler} label="Dimensions" data={<Badge variant="outline" className="border-amber-700/50 text-amber-200">{artwork.dimensions}</Badge>} /></li>}
                      {artwork.location && <li><InfoItem icon={MapPin} label="Lieu" data={<Badge variant="outline" className="border-amber-700/50 text-amber-200">{artwork.location}</Badge>} /></li>}
                      {artwork.year && <li><InfoItem icon={Calendar} label="Année" data={<Badge variant="outline" className="border-amber-700/50 text-amber-200">{artwork.year}</Badge>} /></li>}
                    </ul>
                  </div>
                </div>
              </motion.div>
            </div>

            {artwork.making_of_images && artwork.making_of_images.length > 0 && (
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="mt-20">
                <motion.div variants={slideIn} className="relative inline-block mb-6">
                  <h2 className="text-3xl font-serif font-bold text-amber-100 mb-2 flex items-center">
                    <Clock className="mr-3 text-amber-500" /> Dans les Coulisses de l'Atelier
                  </h2>
                  <div className="absolute -bottom-3 left-0 right-0 h-1 bg-gradient-to-r from-amber-600 to-amber-800"></div>
                  <div className="absolute -bottom-3 left-0 w-16 h-1 bg-amber-500 animate-pulse"></div>
                </motion.div>
                <motion.p variants={fadeIn} className="text-amber-200/80 mb-10 max-w-2xl">Les étapes clés de la création de l'œuvre, de la matière brute à la forme finale.</motion.p>
                
                <div className="relative border-l-2 border-amber-700/50 pl-8 ml-4 space-y-16">
                  {artwork.making_of_images.map((image, index) => (
                    <motion.div 
                      key={index} 
                      variants={scaleIn}
                      className="relative"
                    >
                      <div className="absolute -left-[42px] top-0 w-10 h-10 rounded-full bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center ring-4 ring-black border border-amber-500/20">
                        <span className="font-bold text-amber-100">{index + 1}</span>
                      </div>
                      <div className="relative">
                        <div className="absolute -inset-1 bg-black/50 rounded-xl opacity-80 blur-md"></div>
                        <div className="relative bg-black/30 backdrop-blur-sm rounded-lg border border-amber-900/20 overflow-hidden">
                          <Zoom>
                            <div className="w-full flex items-center justify-center p-4">
                              <img 
                                src={image} 
                                alt={`Making-of ${artwork.title} - Étape ${index + 1}`} 
                                className="block max-w-full h-auto object-contain"
                                loading="lazy"
                              />
                            </div>
                          </Zoom>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                  <div className="absolute -left-[38px] -bottom-3 w-10 h-10 rounded-full bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center ring-4 ring-black border border-amber-500/20">
                    <Check size={20} className="text-amber-100" />
                  </div>
                </div>
              </motion.div>
            )}
          </motion.article>
        </div>
      </div>
    </PublicLayout>
  );
};

// Composant utilitaire pour la fiche technique avec style africain
const InfoItem = ({ icon: Icon, label, data }: { icon: React.ElementType, label: string, data: React.ReactNode }) => (
  <div className="flex items-start group">
    <div className="w-8 h-8 rounded-full bg-amber-900/30 flex items-center justify-center mr-4 mt-1 flex-shrink-0 group-hover:bg-amber-800/40 transition-colors">
      <Icon className="w-4 h-4 text-amber-500" />
    </div>
    <div>
      <span className="block text-amber-200/70 text-sm font-medium">{label}</span>
      <div className="text-amber-100 font-medium mt-1">{data}</div>
    </div>
  </div>
);

export default ArtworkDetail;